"use client";

import { Check, Cloud, Heart, Inbox, Sparkles } from "lucide-react";
import Link from "next/link";
import { SUPPORT_EMAIL } from "@/lib/links";

// Entry animation: the shared `fade-up` keyframe in globals.css, staggered
// per block. The global prefers-reduced-motion rule disables it for users
// who've asked their OS for less motion.
const fadeUp = (delay: number): React.CSSProperties => ({
  animation: `fade-up 0.7s ${delay}s cubic-bezier(0.22,1,0.36,1) backwards`,
});

const cardBase =
  "flex min-w-0 flex-[1_1_min(460px,100%)] flex-col rounded-[28px] p-6 sm:rounded-[36px] sm:p-8";
const cardTitle =
  "font-display text-2xl font-semibold leading-tight tracking-[-0.6px] sm:text-[28px]";
const chip =
  "rounded-full bg-white px-3.5 py-1 text-[13px] font-bold text-brand";

function Mascot() {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      role="img"
      aria-label="Friendly Carnies mascot"
    >
      <circle cx="28" cy="28" r="26" fill="#0950CC" />
      <circle cx="20" cy="24" r="3.5" fill="#FFFFFF" />
      <circle cx="36" cy="24" r="3.5" fill="#FFFFFF" />
      <path
        d="M19 34q9 9 18 0"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="14" cy="32" r="3" fill="#FFB5A7" opacity="0.8" />
      <circle cx="42" cy="32" r="3" fill="#FFB5A7" opacity="0.8" />
    </svg>
  );
}

/** Mini browser-window preview used inside the Landing Page Templates card. */
function PagePreview({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0 flex-[1_1_150px] overflow-hidden rounded-[18px] bg-cream">
      <div className="flex items-center gap-1 px-3 pt-2.5" aria-hidden>
        <span className="h-1.5 w-1.5 rounded-full bg-blush" />
        <span className="h-1.5 w-1.5 rounded-full bg-butter" />
        <span className="h-1.5 w-1.5 rounded-full bg-mint" />
      </div>
      <div className="p-3">{children}</div>
      <div className="px-3 pb-3 text-xs font-semibold text-muted">{label}</div>
    </div>
  );
}

function LandingTemplatesCard() {
  return (
    <article
      style={fadeUp(0.15)}
      className={`${cardBase} sm:min-h-[430px] bg-mist`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={cardTitle}>Landing Page Templates</h3>
        <span className={chip}>Template #1 in build</span>
      </div>
      <p className="mt-2 mb-[22px] text-base text-ink">
        Next.js + Tailwind starters for a cozy SaaS landing, portfolio,
        link-in-bio, or waitlist page.
      </p>
      <div className="mt-auto flex flex-wrap gap-3 rounded-3xl bg-white p-3 sm:p-[18px]">
        <PagePreview label="Portfolio">
          <div className="flex items-center gap-2" aria-hidden>
            <span className="block h-8 w-8 flex-none rounded-full bg-blush" />
            <span className="space-y-1">
              <span className="block h-1.5 w-14 rounded-full bg-navy/70" />
              <span className="block h-1.5 w-9 rounded-full bg-mist" />
            </span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1.5" aria-hidden>
            <span className="aspect-square rounded-lg bg-butter" />
            <span className="aspect-square rounded-lg bg-mist" />
            <span className="aspect-square rounded-lg bg-blush" />
          </div>
        </PagePreview>
        <PagePreview label="Waitlist">
          <div className="text-center" aria-hidden>
            <span className="mx-auto block h-7 w-7 rounded-full bg-mint" />
            <span className="mx-auto mt-2 block h-1.5 w-20 rounded-full bg-navy/70" />
            <span className="mx-auto mt-1.5 block h-1.5 w-14 rounded-full bg-mist" />
            <span className="mx-auto mt-3 flex w-full max-w-[130px] items-center rounded-full bg-white p-1">
              <span className="h-2 flex-1 rounded-full bg-cream" />
              <span className="ml-1 h-4 w-8 rounded-full bg-brand" />
            </span>
          </div>
        </PagePreview>
        <PagePreview label="Link-in-bio">
          <div className="flex flex-col items-center gap-1.5" aria-hidden>
            <span className="block h-8 w-8 rounded-full bg-butter" />
            <span className="block h-5 w-full max-w-[120px] rounded-full bg-blush" />
            <span className="block h-5 w-full max-w-[120px] rounded-full bg-mist" />
            <span className="block h-5 w-full max-w-[120px] rounded-full bg-mint" />
          </div>
        </PagePreview>
      </div>
    </article>
  );
}

const palette = [
  { name: "Cream", cls: "bg-cream ring-1 ring-navy/10" },
  { name: "Mist", cls: "bg-mist" },
  { name: "Butter", cls: "bg-butter" },
  { name: "Blush", cls: "bg-blush" },
  { name: "Mint", cls: "bg-mint" },
  { name: "Navy", cls: "bg-navy" },
  { name: "Blue", cls: "bg-brand" },
];

const kitExtras = [
  { Icon: Inbox, label: "Empty states" },
  { Icon: Cloud, label: "404 pages" },
  { Icon: Sparkles, label: "Onboarding" },
];

function UiKitCard() {
  return (
    <article
      style={fadeUp(0.23)}
      className={`${cardBase} sm:min-h-[430px] bg-butter`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={cardTitle}>Cozy UI Kit</h3>
        <span className={chip}>Illustrated</span>
      </div>
      <p className="mt-2 mb-[22px] text-base text-ink">
        Components and a design system with illustrated empty states, 404
        pages, and onboarding screens.
      </p>
      <div
        className="mt-auto flex flex-col gap-4 rounded-3xl bg-white p-4 sm:p-5"
        aria-hidden
      >
        {/* Buttons + toggle */}
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="rounded-full bg-brand px-4 py-2 text-[13px] font-semibold text-white">
            Get started
          </span>
          <span className="rounded-full bg-butter px-4 py-2 text-[13px] font-semibold text-navy">
            Learn more
          </span>
          <span className="rounded-full px-4 py-2 text-[13px] font-semibold text-navy ring-1 ring-navy/20">
            Cancel
          </span>
          <span className="ml-auto flex h-6 w-11 items-center justify-end rounded-full bg-brand p-0.5">
            <span className="h-5 w-5 rounded-full bg-white" />
          </span>
        </div>

        {/* Component card */}
        <div className="flex items-center gap-3 rounded-[18px] bg-cream p-3.5">
          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-blush">
            <Heart className="h-5 w-5 text-navy" strokeWidth={1.8} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold">Cozy profile card</div>
            <div className="text-xs text-muted">Soft corners, warm shadows</div>
          </div>
          <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-mint">
            <Check className="h-3.5 w-3.5 text-mint-ink" strokeWidth={3} />
          </span>
        </div>

        {/* Palette + type sample */}
        <div className="flex items-center gap-3">
          <div className="flex flex-wrap gap-1.5">
            {palette.map((c) => (
              <span
                key={c.name}
                title={c.name}
                className={`h-7 w-7 rounded-full ${c.cls}`}
              />
            ))}
          </div>
          <span className="ml-auto font-display text-[28px] font-semibold leading-none tracking-[-0.5px]">
            Aa
          </span>
        </div>

        {/* Included screens */}
        <ul className="flex flex-wrap gap-2 border-t border-navy/10 pt-3.5">
          {kitExtras.map(({ Icon, label }) => (
            <li
              key={label}
              className="inline-flex items-center gap-1.5 rounded-full bg-mist px-3 py-1.5 text-xs font-semibold"
            >
              <Icon className="h-3.5 w-3.5 text-brand" strokeWidth={2} />
              {label}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

const templateChips = [
  { label: "Notion templates", active: false },
  { label: "Canva templates", active: true },
];

function TemplatesCard() {
  return (
    <article
      style={fadeUp(0.31)}
      className={`${cardBase} sm:min-h-[380px] bg-blush`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={cardTitle}>Notion &amp; Canva Templates</h3>
        <span className={chip}>In build</span>
      </div>
      <p className="mt-2 mb-[22px] text-base text-ink">
        Planners, trackers, and social templates in a cozy style, ready to
        drop in.
      </p>
      <ul className="mt-auto flex flex-wrap gap-2.5">
        {templateChips.map((c) => (
          <li
            key={c.label}
            className={`rounded-full px-5 py-2.5 text-[15px] font-semibold ${
              c.active ? "bg-brand text-white" : "bg-white"
            }`}
          >
            {c.label}
          </li>
        ))}
      </ul>
    </article>
  );
}

function IllustrationPacksCard() {
  return (
    <article
      style={fadeUp(0.39)}
      className={`${cardBase} sm:min-h-[380px] bg-navy text-cream`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={cardTitle}>Illustration &amp; Icon Packs</h3>
        <span className="rounded-full bg-cream px-3.5 py-1 text-[13px] font-bold text-brand">
          In build
        </span>
      </div>
      <p className="mt-2 mb-[22px] text-base text-[#c8d2e6]">
        Hand-drawn SVG and PNG art with a commercial licence.
      </p>
      <div className="mt-auto flex flex-wrap gap-3.5">
        <div className="min-w-0 flex-[1_1_180px] rounded-[22px] bg-navy-soft p-[18px]">
          <div className="mb-1.5 font-display text-[19px] font-semibold">
            Commercial licence
          </div>
          <div className="text-sm text-[#c8d2e6]">
            Use it in client and personal projects.
          </div>
        </div>
        <div className="min-w-0 flex-[1_1_180px] rounded-[22px] bg-navy-soft p-[18px]">
          <div className="mb-1.5 font-display text-[19px] font-semibold">
            React-ready SVG
          </div>
          <div className="text-sm text-[#c8d2e6]">
            Tree-shakeable icons and illustrations.
          </div>
        </div>
      </div>
    </article>
  );
}

export function ComingSoon() {
  return (
    <>
      {/* HERO */}
      <section className="mx-auto max-w-[920px] px-5 pt-12 pb-12 text-center sm:px-8 sm:pt-[72px] sm:pb-14">
        <div style={fadeUp(0.05)}>
          <span className="inline-flex items-center gap-2.5 rounded-full bg-mist px-[18px] py-2 text-sm font-semibold text-brand">
            <span className="h-2 w-2 rounded-full bg-brand" />
            Launching 2026
          </span>
        </div>

        <h1
          style={fadeUp(0.12)}
          className="mt-[22px] mb-[22px] font-display text-[clamp(38px,9.5vw,92px)] font-semibold leading-none tracking-[-1.5px] sm:tracking-[-2.5px]"
        >
          Ready-to-deploy
          <br />
          digital products<span className="text-brand">.</span>
        </h1>

        <p
          style={fadeUp(0.2)}
          className="mx-auto mb-[34px] max-w-[620px] text-pretty text-lg text-ink sm:text-xl"
        >
          A cozy storefront for landing page templates, UI kits, Notion and
          Canva templates, and illustration packs. Built for indie devs, small
          creators and makers.
        </p>

        <a
          href="#join"
          style={fadeUp(0.28)}
          className="inline-block rounded-full bg-brand px-8 py-4 font-display text-lg font-semibold text-white shadow-[0_12px_24px_rgba(9,80,204,0.25)] transition-colors hover:bg-[#0742a8]"
        >
          Notify Me on Launch
        </a>
        <Link
          href="/shop"
          style={fadeUp(0.32)}
          className="mt-2 inline-block px-3 py-3 font-semibold text-brand underline-offset-4 hover:underline sm:mt-0 sm:ml-3"
        >
          Browse the shop
        </Link>

        <ul
          style={fadeUp(0.36)}
          className="mt-[34px] flex flex-wrap justify-center gap-x-2.5 gap-y-2.5 text-[13px] font-semibold text-ink sm:gap-x-3.5 sm:text-sm"
        >
          {[
            "Secure card checkout",
            "Instant download",
            "Lifetime updates",
            "Email support",
          ].map((t) => (
            <li key={t} className="rounded-full bg-white px-4 py-[7px]">
              {t}
            </li>
          ))}
        </ul>
      </section>

      {/* PRODUCT LINES */}
      <section className="mx-auto max-w-[1200px] px-5 pt-8 pb-6 sm:px-8 sm:pt-10">
        <div className="mb-8 flex flex-wrap items-center gap-4">
          <Mascot />
          <h2 className="font-display text-[clamp(28px,3.6vw,42px)] font-semibold leading-[1.1] tracking-[-1px]">
            Launching with four product lines:
          </h2>
        </div>
        <div className="flex flex-wrap gap-4 sm:gap-5">
          <LandingTemplatesCard />
          <UiKitCard />
          <TemplatesCard />
          <IllustrationPacksCard />
        </div>
      </section>

      {/* JOIN THE LAUNCH LIST */}
      <section
        id="join"
        className="mx-auto max-w-[1200px] scroll-mt-6 px-5 pt-12 sm:px-8 sm:pt-[72px]"
      >
        <div className="rounded-[32px] bg-brand px-6 py-12 text-center sm:rounded-[44px] sm:px-10 sm:py-16 text-white">
          <h2 className="mb-3 font-display text-[clamp(30px,6vw,56px)] font-semibold leading-[1.05] tracking-[-1px] sm:tracking-[-1.5px]">
            Join the launch list.
          </h2>
          <p className="mx-auto mb-7 max-w-[460px] text-lg">
            Get early access to our first catalog.
          </p>
          {/* The submit handler builds the mailto URL at click-time so the
              page source never contains a `mailto:` action. Lighthouse /
              Chrome flag those as "insecure URL" on HTTPS pages even though
              they're harmless. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              const email = String(data.get("email") ?? "").trim();
              if (!email) return;
              const body = `Please notify me at ${email} when carnies. launches.`;
              window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Launch list")}&body=${encodeURIComponent(body)}`;
            }}
            className="mx-auto flex max-w-[520px] flex-wrap justify-center gap-3"
          >
            <label htmlFor="launch-email" className="sr-only">
              Email address
            </label>
            <input
              id="launch-email"
              type="email"
              name="email"
              required
              placeholder="you@email.com"
              className="h-14 min-w-0 flex-[1_1_min(220px,100%)] rounded-full border-0 bg-white px-6 text-base text-navy outline-none placeholder:text-muted focus-visible:ring-4 focus-visible:ring-white/40"
            />
            <button
              type="submit"
              className="h-14 w-full rounded-full bg-butter px-7 sm:w-auto font-display text-[17px] font-semibold text-navy transition-colors hover:bg-[#ffe8a8]"
            >
              Notify me
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
